/**
 * Field alerts: native geofences around nearby fields, opt-in per device.
 *
 * The phone's OS watches the boundaries and wakes native code on enter/exit,
 * app open or not. The geofencing plugin then POSTs the transition to the
 * geofence-event edge function with this device's token; the server decides
 * whether to send a "connect?" push (enter) or disconnect the device (exit).
 * Entering never connects by itself — that choice stays with the user.
 *
 * iOS monitors at most 20 regions per app, so we register the nearest ones and
 * re-sync whenever the field list changes.
 */
import { isNative } from './supabase.js';
import { devices as devicesApi } from './api.js';

const MAX_REGIONS = 18;           // leave headroom under the iOS limit of 20
const MIN_RADIUS_M = 60;          // regions much smaller than this fire unreliably

const plugin = (name) => window.Capacitor?.Plugins?.[name] ?? null;

export function fieldAlertsSupported() {
  return isNative() && Boolean(plugin('BackgroundGeolocation'));
}

/** iOS shows Connect / Not now on the "you are in a field" push. */
async function registerActions() {
  const LN = plugin('LocalNotifications');
  await LN?.registerActionTypes?.({
    types: [
      { id: 'FIELD_ENTER', actions: [
        { id: 'connect', title: 'Connect', foreground: true },
        { id: 'dismiss', title: 'Not now', destructive: false },
      ] },
      { id: 'FIELD_EXIT', actions: [{ id: 'tap', title: 'Open', foreground: true }] },
    ],
  }).catch(() => {});
}

/**
 * Turn alerts on for this device. Asks for Always location (the OS needs it
 * to watch regions while the app sleeps). Resolves to the permission status
 * string so the UI can explain a refusal.
 */
export async function enableFieldAlerts(device, fields = []) {
  const BG = plugin('BackgroundGeolocation');
  if (!BG || !device) throw new Error('Field alerts are only available in the installed app.');

  const token = await devicesApi.setFieldAlerts(device.id, true);
  await registerActions();
  await BG.setupGeofencing({
    url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/geofence-event`,
    headers: { 'x-geofence-token': token, 'Content-Type': 'application/json' },
    payload: { device: device.id },
    notifyOnEntry: true,
    notifyOnExit: true,
    requestPermissions: true,
    backgroundLocation: true,      // Android: ACCESS_BACKGROUND_LOCATION is in the manifest
  });
  const perm = await BG.checkPermissions().catch(() => null);
  await syncGeofences(fields, true);
  return perm;
}

export async function disableFieldAlerts(device) {
  const BG = plugin('BackgroundGeolocation');
  await BG?.removeAllGeofences?.().catch(() => {});
  if (device) await devicesApi.setFieldAlerts(device.id, false);
}

/** Keep the monitored regions equal to the nearest live fields. */
export async function syncGeofences(fields, enabled) {
  const BG = plugin('BackgroundGeolocation');
  if (!BG || !enabled) return;
  const wanted = fields
    .filter((f) => (f.status === 'active' || f.status === 'building') && (f.is_public || f.is_mine))
    .sort((a, b) => (a.distance_m ?? 0) - (b.distance_m ?? 0))
    .slice(0, MAX_REGIONS);
  const have = new Set(((await BG.getMonitoredGeofences?.().catch(() => null))?.geofences ?? [])
    .map((g) => g.identifier));
  const want = new Set(wanted.map((f) => f.id));
  for (const id of have) if (!want.has(id)) await BG.removeGeofence({ identifier: id }).catch(() => {});
  for (const f of wanted) {
    if (have.has(f.id)) continue;
    await BG.addGeofence({
      identifier: f.id, latitude: f.latitude, longitude: f.longitude,
      radius: Math.max(MIN_RADIUS_M, f.radius_m + 20),
      payload: { field: f.id },
    }).catch((e) => console.warn('geofence add failed', f.id, e));
  }
}

/** Foreground transitions (app open): let the page refresh itself. */
export function onGeofenceTransition(cb) {
  const BG = plugin('BackgroundGeolocation');
  if (!BG?.addListener) return () => {};
  const h = BG.addListener('geofenceTransition', cb);
  return () => Promise.resolve(h).then((x) => x?.remove?.());
}
